// src/app/pages/TopUpPage/index.tsx
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, Coins, Sparkles } from "lucide-react";
import { useNavigate } from "react-router";
import { motion } from "motion/react";
import { runWithNetworkLifecycle, type NetworkLifecycleCallbacks } from "@/app/shared/lifecycle";
import { TopUpPageController } from "./controller/TopUpPageController";
import { PaymentsApi, type TokenPackageFromCatalog } from '@/app/data/profilPayment/api/PaymentsApi';
import { Button } from "@/app/core/ui/neitral.ui/button";
import { GridSkeleton } from "@/app/core/ui/skeletons/GridSkeleton";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";
import { AppInitializationController, InitializationState } from '@/app/shared/app-init/AppInitializationController';

const safeHaptic = (callback: () => void) => {
  try { callback(); } catch {}
};

interface TopUpPageProps {
  lifecycle?: NetworkLifecycleCallbacks;
}

export function TopUpPage({ lifecycle }: TopUpPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const controller = useMemo(() => new TopUpPageController(), []);
  const paymentsApi = useMemo(() => new PaymentsApi(), []);
  const appInit = AppInitializationController.getInstance();
  const [currentBalance, setCurrentBalance] = useState<number | null>(null);
  const [tokenPackages, setTokenPackages] = useState<TokenPackageFromCatalog[]>([]);
  const [catalogError, setCatalogError] = useState("");
  const [selectedPackage, setSelectedPackage] = useState<string>("");
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(true);
  const selectedPack = tokenPackages.find((p) => p.code === selectedPackage);

  // Загрузка каталога и баланса только при READY
  useEffect(() => {
    if (appInit.state !== InitializationState.READY) {
      return;
    }

    let isMounted = true;
    const loadCatalogAndBalance = async () => {
      setIsLoadingCatalog(true);
      try {
        const packs = await runWithNetworkLifecycle({
          source: "TopUpCatalogLoad",
          lifecycle,
          task: () => paymentsApi.getTokenPackages(),
          onError: (message) => {
            if (!isMounted) return;
            setCatalogError(message);
          },
        });

        if (!isMounted) return;

        if (!packs?.length) {
          setTokenPackages([]);
          setSelectedPackage("");
        } else {
          setCatalogError("");
          setTokenPackages(packs);
          // Выбираем первый пакет только если ни один не выбран
          if (!selectedPackage || !packs.some((pack) => pack.code === selectedPackage)) {
            setSelectedPackage(packs[0].code);
          }
        }
      } finally {
        if (isMounted) setIsLoadingCatalog(false);
      }

      // Баланс загружаем параллельно
      try {
        const balance = await paymentsApi.getBalance();
        if (isMounted) setCurrentBalance(balance.currentTokens);
      } catch {
        // оставляем null
      }
    };

    loadCatalogAndBalance();
    return () => { isMounted = false; };
  }, [paymentsApi, lifecycle, appInit.state]); // ← убрана зависимость selectedPackage

  const handleBuyNow = () => {
    safeHaptic(() => controller.triggerImpact());
    if (selectedPack) {
      const checkoutState = controller.mapTokenPackageToCheckout(selectedPack);
      navigate("/checkout", { state: checkoutState });
    }
  };

  const handleSelectPackage = (packageCode: string) => {
    safeHaptic(() => controller.triggerSelection());
    setSelectedPackage(packageCode);
  };

  const getRubPrice = (pack: TokenPackageFromCatalog) => {
    const rub = pack.prices.find(p => p.currency === 'RUB');
    return rub ? rub.amountMinor / 100 : 0;
  };

  const getPricePer100 = (pack: TokenPackageFromCatalog) => {
    const rub = pack.prices.find(p => p.currency === 'RUB');
    if (!rub || pack.tokenAmount === 0) return 0;
    return (rub.amountMinor / 100 / pack.tokenAmount) * 100;
  };

  return (
    <div className="min-h-screen bg-transparent pb-32 overflow-hidden">
      {/* Хедер */}
      <div
        className="sticky top-0 z-40 px-4 py-4 flex items-center overflow-hidden"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 8px)" }}
      >
        <div className="flex-shrink-0">
          <button onClick={() => navigate(-1)} className="-ml-2 rounded-lg p-2 transition-all hover:bg-white/20 dark:hover:bg-white/10 active:scale-95">
            <ArrowLeft className="h-5 w-5 text-foreground" />
          </button>
        </div>
        <div className="flex-1 flex justify-center">
          <h1 className="text-lg font-bold text-foreground">{t('buyTokens')}</h1>
        </div>
        <div className="flex-shrink-0">
          <div className="flex items-center gap-2 rounded-full bg-white/30 dark:bg-white/10 px-3 py-1.5 backdrop-blur-md border border-white/30 dark:border-white/10">
            <Coins className="h-4 w-4 text-violet-600" />
            <span className="text-sm font-semibold text-foreground">
              {currentBalance !== null ? currentBalance.toLocaleString() : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Контент */}
      <div className="px-4 pt-2">
        <div className="mb-6 text-center">
          <p className="text-sm text-muted-foreground">{t('chooseTokenPackage')}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t('saveMore')}</p>
          {catalogError && <p className="mt-2 text-xs text-destructive">{t('catalogUnavailable')}{catalogError}</p>}
        </div>

        {isLoadingCatalog ? (
          <GridSkeleton columns={1} rows={2} cardHeight="h-48" />
        ) : tokenPackages.length === 0 ? (
          <div className="mb-8 text-center text-sm text-muted-foreground">{t('noProducts')}</div>
        ) : (
          <div className="mb-8 flex flex-col gap-3 md:gap-4">
            {tokenPackages.map((pack, index) => {
              const tokenAmount = pack.tokenAmount;
              const finalPrice = getRubPrice(pack);
              const pricePer100 = getPricePer100(pack);
              const isSelected = selectedPackage === pack.code;
              const bestValue = tokenAmount >= 1000;

              return (
                <motion.div
                  key={pack.code}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleSelectPackage(pack.code)}
                  className="relative w-full cursor-pointer"
                >
                  {bestValue && (
                    <div className="absolute -top-2 right-3 z-10">
                      <div className="flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-md md:px-3 md:py-1 md:text-xs">
                        <Sparkles className="h-2.5 w-2.5 md:h-3 md:w-3" />
                        <span className="hidden sm:inline">{t('bestValue')}</span>
                        <span className="sm:hidden">{t('best')}</span>
                      </div>
                    </div>
                  )}
                  <div
                    className={`relative flex items-center justify-between rounded-xl border-2 bg-card p-4 transition-all md:rounded-2xl md:p-5 ${
                      isSelected ? "border-violet-500 shadow-lg shadow-violet-100" : "border-border shadow-sm hover:border-secondary"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-4">
                        <div className={`flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all md:h-6 md:w-6 ${
                          isSelected ? "border-violet-600 bg-violet-600" : "border-border bg-card"
                        }`}>
                          {isSelected && <Check className="h-3 w-3 text-white md:h-4 md:w-4" />}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xl font-bold text-foreground md:text-3xl">{tokenAmount.toLocaleString()}</span>
                          <span className="text-[10px] text-muted-foreground md:text-sm">{t('tokens')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <div className="text-lg font-bold text-violet-600 md:text-2xl">{controller.formatPrice(finalPrice)}</div>
                      <div className="text-[10px] text-muted-foreground md:text-xs">{controller.formatPrice(pricePer100)} {t('pricePer100')}</div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Кнопка покупки */}
      {selectedPack && (
        <div className="fixed bottom-0 left-0 right-0 z-50 box-shadow mx-2 mb-2 rounded-2xl shadow-2xl" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 30px)" }}>
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
            <Button
              variant="primary"
              size="lg"
              className="mx-auto w-full md:max-w-md bg-violet-600 hover:bg-violet-700 active:scale-98 shadow-lg"
              onClick={handleBuyNow}
            >
              <span>{t('buyNow')}</span>
              <span className="mx-1 text-white/90">·</span>
              <span>{controller.formatPrice(getRubPrice(selectedPack))}</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}